import { useState } from "react";
import HomeNavbar from "../components/HomeNavbar";
import AppURL from "../components/AppUrl";



interface AboutUsProps {
    language: string;
    setLanguage: (language: string) => void;

}
const AboutUs = ({ language, setLanguage }: AboutUsProps) => {
    const [isModalOpen, setIsModalOpen] = useState(false); // Ajoutez cette ligne
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        phone: '',
        message: ''
    });

    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!formData.fullName || !formData.email || !formData.message) {
            setError(language === "fr"
                ? "Veuillez remplir tous les champs obligatoires"
                : "Please fill all required fields");
            return;
        }

        if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
            setError(language === "fr"
                ? "Veuillez entrer une adresse email valide"
                : "Please enter a valid email address");
            return;
        }

        setIsLoading(true);

        try {
            const response = await fetch(`${AppURL}/api/ContactUs/Create`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(formData)
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || "Une erreur s'est produite");
            }

            setIsSuccess(true);
            setFormData({
                fullName: '',
                email: '',
                phone: '',
                message: ''
            });

            setTimeout(() => setIsSuccess(false), 3000);
        } catch (err: any) {
            setError(err.message || "Une erreur s'est produite");
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    return (
        <div className="relative bg-green-50">
            {/* Navbar */}
            <HomeNavbar language={language} setLanguage={setLanguage} />

            {/* Hero Section */}
            <div className="pt-32 pb-20 px-6 max-w-6xl mx-auto">
                <h1 className="text-4xl font-bold text-green-950 mb-12 text-center">
                    {language === "fr" ? "À propos de nous" : "About Us"}
                </h1>

                <div className="grid md:grid-cols-2 gap-12">
                    {/* Left Column - Contact Info */}
                    <div className="bg-white p-8 rounded-2xl shadow-lg">
                        <h2 className="text-2xl font-semibold text-green-950 mb-6">
                            {language === "fr" ? "Contactez-nous" : "Contact Us"}
                        </h2>


                        {/* Special Requests */}
                        <div className="mb-8">
                            <h3 className="text-lg font-medium text-green-800 mb-3">
                                {language === "fr" ? "Demandes spéciales" : "Special Requests"}
                            </h3>
                            <p className="text-gray-600 mb-4">
                                {language === "fr"
                                    ? "Des restrictions alimentaires ? Des questions sur un événement ? Contactez-nous et nous vous répondrons rapidement."
                                    : "Do you have dietary concerns? Questions about an upcoming event? Drop us a line, and we'll get back to you soon."}
                            </p>

                        </div>
                        {/* Ajoutez ce bouton ici */}
                        <button
                            onClick={() => {
                                setIsModalOpen(true);
                                setIsSuccess(false);
                                setError('');
                            }}
                            className="bg-green-700 hover:bg-green-800 text-white px-6 py-2 rounded-lg mb-6 transition-colors"
                        >
                            {language === "fr" ? "Nous contacter" : "Contact Us"}
                        </button>

                        {/* Address */}
                        <div className="mb-8">
                            <h3 className="text-lg font-medium text-green-800 mb-3">
                                {language === "fr" ? "Adresse" : "Address"}
                            </h3>
                            <p className="text-gray-600">
                                Chèvrefeuille-Lonicera.ca<br />
                                1867 Wellington Street, Montreal, QC, Canada
                            </p>
                        </div>

                        {/* Phone */}
                        <div className="mb-8">
                            <h3 className="text-lg font-medium text-green-800 mb-3">
                                {language === "fr" ? "Téléphone" : "Phone"}
                            </h3>
                            <p className="text-gray-600">(438) 872-0612</p>
                        </div>

                        {/* Social Media */}
                        <div>
                            <h3 className="text-lg font-medium text-green-800 mb-3">
                                {language === "fr" ? "Réseaux sociaux" : "Social"}
                            </h3>
                            <div className="flex space-x-4">
                                {/* Facebook Icon */}
                                <a
                                    href="https://www.facebook.com/share/14xgHchKy1/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-green-700 hover:text-green-950 transition-colors"
                                    aria-label="Facebook"
                                >
                                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
                                    </svg>
                                </a>

                                {/* Instagram Icon */}
                                <a
                                    href="https://www.instagram.com/chevrefeuille.lonicera?igsh=MTc5dmFzcDZxaTRrbg=="
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-green-700 hover:text-green-950 transition-colors"
                                    aria-label="Instagram"
                                >
                                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                                    </svg>
                                </a>
                            </div>
                        </div>
                    </div>

                    {/* Right Column - Hours */}
                    <div className="bg-white p-8 rounded-2xl shadow-lg">
                        <h2 className="text-2xl font-semibold text-green-950 mb-6">
                            {language === "fr" ? "Heures d'ouverture" : "Opening Hours"}
                        </h2>

                        <div className="space-y-4">
                            {[
                                { day: "lun.", enDay: "Mon", hours: "10 h 00 – 16 h 00", closed: false },
                                { day: "mar.", enDay: "Tue", hours: "", closed: true },
                                { day: "mer.", enDay: "Wed", hours: "", closed: true },
                                { day: "jeu.", enDay: "Thu", hours: "", closed: true },
                                { day: "ven.", enDay: "Fri", hours: "17 h 00 – 22 h 00", closed: false },
                                { day: "sam.", enDay: "Sat", hours: "10 h 00 – 16 h 00", closed: false },
                                { day: "dim.", enDay: "Sun", hours: "10 h 00 – 16 h 00", closed: false }
                            ].map((item, index) => (
                                <div key={index} className="flex justify-between border-b border-gray-100 pb-2">
                                    <span className="font-medium text-green-800">
                                        {language === "fr" ? item.day : item.enDay}
                                    </span>
                                    <span className={item.closed ? "text-gray-400" : "text-gray-600"}>
                                        {item.closed
                                            ? (language === "fr" ? "Fermé" : "Closed")
                                            : item.hours}
                                    </span>
                                </div>
                            ))}
                        </div>

                        {/* Map Embed */}
                        <div className="mt-8 rounded-xl overflow-hidden shadow-md">
                            <iframe
                                title="Google Maps"
                                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2796.044144777754!2d-73.57082268444007!3d45.50393397910143!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4cc91a6a879e9a5d%3A0x1b9c3a5f9e8e5b1d!2s1867%20Wellington%20St%2C%20Montreal%2C%20QC%20H3K%202W8!5e0!3m2!1sen!2sca!4v1620000000000!5m2!1sen!2sca"
                                width="100%"
                                height="200"
                                style={{ border: 0 }}
                                allowFullScreen
                                loading="lazy"
                                className="rounded-lg"
                            ></iframe>
                        </div>


                    </div>
                </div>
            </div>

            {/* Footer */}
            <footer className="bg-green-800 text-white py-8 px-4 text-center">
                <p className="text-sm">
                    {language === "fr"
                        ? `© ${new Date().getFullYear()} Chèvrefeuille-Lonicera. Tous droits réservés.`
                        : `© ${new Date().getFullYear()} Chèvrefeuille-Lonicera. All rights reserved.`}
                </p>
            </footer>

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-green-50 bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-semibold text-green-950">
                                {language === "fr" ? "Contactez-nous" : "Contact Us"}
                            </h3>
                            <button
                                onClick={() => setIsModalOpen(false)}
                                aria-label={language === "fr" ? "Fermer" : "Close"}
                                className="text-white bg-red-500 hover:bg-red-600 rounded-full w-8 h-8 flex items-center justify-center transition duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-red-300"
                            >
                                &times;
                            </button>
                        </div>



                        <form className="space-y-4">
                            <div>
                                <label htmlFor="fullName" className="block text-sm font-medium text-gray-700">
                                    {language === "fr" ? "Nom complet" : "Full Name"}
                                </label>
                                <input
                                    type="text"
                                    id="fullName"
                                    name="fullName"
                                    value={formData.fullName}
                                    onChange={handleInputChange}
                                    className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                                    required
                                />
                            </div>

                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                                    Email
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                                    required
                                />
                            </div>

                            <div>
                                <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                                    {language === "fr" ? "Téléphone" : "Phone"}
                                </label>
                                <input
                                    type="tel"
                                    id="phone"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                                />
                            </div>

                            <div>
                                <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                                    {language === "fr" ? "Comment pouvons-nous vous aider ?" : "How can we help you?"}
                                </label>
                                <textarea
                                    id="message"
                                    name="message"
                                    rows={4}
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                                    required
                                ></textarea>
                            </div>

                            <button
                                type="button"
                                onClick={handleSubmit}
                                disabled={isLoading}
                                className="w-full bg-green-700 hover:bg-green-800 text-white py-2 px-4 rounded-md transition-colors disabled:bg-green-500 flex items-center justify-center"
                            >
                                {isLoading ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        {language === "fr" ? "Envoi en cours..." : "Sending..."}
                                    </>
                                ) : (
                                    language === "fr" ? "Envoyer" : "Submit"
                                )}
                            </button>

                            {error && (
                                <div className="text-red-500 text-sm p-2 bg-red-50 rounded-md">
                                    {error}
                                </div>
                            )}

                            {isSuccess && (
                                <div className="text-green-700 text-sm p-2 bg-green-50 rounded-md">
                                    {language === "fr"
                                        ? "Merci ! Votre message a été envoyé avec succès."
                                        : "Thank you! Your message has been sent successfully."}
                                </div>
                            )}
                        </form>
                    </div>
                </div>
            )}


        </div>
    );
};

export default AboutUs;